(() => {
  "use strict";

  const FORM_SELECTOR = "[data-voigt-quote-form]";
  const INITIALIZED_ATTRIBUTE = "data-voigt-quote-initialized";

  const FIRST_TOUCH_KEY = "voigt_quote_first_touch_v1";
  const LATEST_TOUCH_KEY = "voigt_quote_latest_touch_v1";

  const ATTRIBUTION_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "gclid",
    "gbraid",
    "wbraid",
    "fbclid",
    "ttclid",
    "msclkid",
  ];

  const CLICK_ID_KEYS = [
    "gclid",
    "gbraid",
    "wbraid",
    "fbclid",
    "ttclid",
    "msclkid",
  ];

  function getFields(form, name) {
    return Array.from(
      form.querySelectorAll(`[name="${name}"]`),
    );
  }

  function getField(form, name) {
    return getFields(form, name)[0] || null;
  }

  function getFieldValue(form, name) {
    const fields = getFields(form, name);
    const field = fields[0];

    if (!field) {
      return "";
    }

    if (field.type === "radio" || field.type === "checkbox") {
      return fields.find((element) => element.checked)?.value || "";
    }

    if (typeof field.value === "string") {
      return field.value;
    }

    return "";
  }

  function setFieldValue(form, name, value) {
    const field = getField(form, name);

    if (field && "value" in field) {
      field.value = value == null ? "" : String(value);
    }
  }

  function getStoredValue(key) {
    try {
      const value = window.localStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return null;
    }
  }

  function setStoredValue(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Storage can be unavailable in restricted browsers.
    }
  }

  /**
   * Retains only marketing attribution query parameters.
   * This helps prevent unrelated URL data from entering the lead payload.
   */
  function sanitizeUrl(value) {
    if (!value) {
      return "";
    }

    try {
      const url = new URL(value, window.location.origin);
      const permittedParameters = new URLSearchParams();

      ATTRIBUTION_KEYS.forEach((key) => {
        const parameterValue = url.searchParams.get(key);

        if (parameterValue) {
          permittedParameters.set(key, parameterValue);
        }
      });

      url.search = permittedParameters.toString();
      url.hash = "";

      return url.toString();
    } catch (error) {
      return "";
    }
  }

  function getCurrentAttributionParameters() {
    const parameters = new URLSearchParams(window.location.search);
    const attribution = {};

    ATTRIBUTION_KEYS.forEach((key) => {
      attribution[key] = parameters.get(key) || "";
    });

    return attribution;
  }

  function hasAttributionSignal(attribution) {
    return ATTRIBUTION_KEYS.some((key) => Boolean(attribution[key]));
  }

  function getReferrerSource(referrer) {
    if (!referrer) {
      return "direct";
    }

    try {
      const referrerUrl = new URL(referrer);

      if (referrerUrl.hostname === window.location.hostname) {
        return "internal";
      }

      return referrerUrl.hostname.replace(/^www\./, "");
    } catch (error) {
      return "referral";
    }
  }

  function buildCurrentTouch() {
    const parameters = getCurrentAttributionParameters();
    const referrer = sanitizeUrl(document.referrer);

    return {
      ...parameters,

      source:
        parameters.utm_source ||
        CLICK_ID_KEYS.find((key) => parameters[key]) ||
        getReferrerSource(referrer),

      landing_page: sanitizeUrl(window.location.href),
      referrer,
      captured_at: new Date().toISOString(),
    };
  }

  function getAttribution() {
    const currentTouch = buildCurrentTouch();

    const existingFirstTouch = getStoredValue(FIRST_TOUCH_KEY);
    const existingLatestTouch = getStoredValue(LATEST_TOUCH_KEY);

    const firstTouch = existingFirstTouch || currentTouch;
    let latestTouch = existingLatestTouch || currentTouch;

    if (
      hasAttributionSignal(currentTouch) ||
      (!existingLatestTouch && currentTouch.source !== "internal")
    ) {
      latestTouch = currentTouch;
    }

    if (!existingFirstTouch) {
      setStoredValue(FIRST_TOUCH_KEY, firstTouch);
    }

    setStoredValue(LATEST_TOUCH_KEY, latestTouch);

    const activeTouch = hasAttributionSignal(currentTouch)
      ? currentTouch
      : latestTouch;

    const attribution = {};

    ATTRIBUTION_KEYS.forEach((key) => {
      attribution[key] = activeTouch?.[key] || "";
    });

    return {
      ...attribution,

      landing_page: firstTouch?.landing_page || "",
      referring_page: firstTouch?.referrer || "",
      current_page: sanitizeUrl(window.location.href),

      first_touch_source: firstTouch?.source || "direct",
      latest_touch_source: latestTouch?.source || "direct",

      first_touch_captured_at: firstTouch?.captured_at || "",
      latest_touch_captured_at: latestTouch?.captured_at || "",
    };
  }

  function getProductContext(form) {
    return {
      product_id:
        getFieldValue(form, "product_id") ||
        form.dataset.productId ||
        "",
      variant_id:
        getFieldValue(form, "variant_id") ||
        form.dataset.variantId ||
        "",
      sku: getFieldValue(form, "sku"),

      product_title: getFieldValue(form, "product_title"),
      product_handle: getFieldValue(form, "product_handle"),
      product_url: getFieldValue(form, "product_url"),

      vendor: getFieldValue(form, "vendor"),
      product_type: getFieldValue(form, "product_type"),

      section_id: getFieldValue(form, "section_id"),
      block_id: getFieldValue(form, "block_id"),
    };
  }

  /**
   * Sends non-PII event information to GTM/dataLayer.
   *
   * Customer names, email addresses, phone numbers, company names,
   * and message contents are intentionally excluded.
   */
  function pushAnalyticsEvent(eventName, form, additionalData = {}) {
    // Preserve the live storefront's confirmed-lead tracking integration.
    if (eventName === "generate_lead") {
      try {
        window.PdsEquipmentTracking?.trackLead(additionalData.quote_request_id);
      } catch { /* Analytics must never prevent lead intake. */ }
    }
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: eventName,

      quote_request: {
        form_name: "product_quote_request",
        lead_source: "Shopify",

        ...getProductContext(form),
        ...additionalData,
      },
    });
  }

  function setFormStatus(form, state, message = "") {
    const successElement = form.querySelector("[data-quote-success]");
    const errorElement = form.querySelector("[data-quote-error]");

    if (successElement) {
      successElement.hidden = state !== "success";
    }

    if (errorElement) {
      errorElement.hidden = state !== "error";

      if (state === "error" && message) {
        errorElement.textContent = message;
      }
    }

    const focusTarget =
      state === "success" ? successElement : errorElement;

    if (focusTarget && state !== "idle") {
      focusTarget.focus({
        preventScroll: true,
      });

      focusTarget.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }

  function setSubmitting(form, isSubmitting) {
    const submitButton = form.querySelector("[data-quote-submit]");
    const submitLabel = form.querySelector("[data-quote-submit-label]");
    const loadingLabel = form.querySelector("[data-quote-submit-loading]");

    if (submitButton) {
      submitButton.disabled = isSubmitting;
      submitButton.setAttribute("aria-busy", String(isSubmitting));
    }

    if (submitLabel) {
      submitLabel.hidden = isSubmitting;
    }

    if (loadingLabel) {
      loadingLabel.hidden = !isSubmitting;
    }
  }

  function buildPayload(form) {
    const marketingConsent = getField(form, "marketing_consent");

    return {
      quote_request_id:
        form.dataset.quoteRequestId || createQuoteRequestId(),
      form_name:
        getFieldValue(form, "form_name") ||
        "product_quote_request",
      lead_source:
        getFieldValue(form, "lead_source") ||
        "Shopify",

      /**
       * The server rejects the request if this field contains a value.
       */
      honeypot: getFieldValue(form, "website"),

      submitted_at: new Date().toISOString(),

      contact: {
        full_name: getFieldValue(form, "full_name").trim(),
        first_name: getFieldValue(form, "first_name").trim(),
        last_name: getFieldValue(form, "last_name").trim(),
        email: getFieldValue(form, "email").trim(),
        phone: getFieldValue(form, "phone").trim(),
        postal_code: getFieldValue(form, "postal_code").trim(),
        company: getFieldValue(form, "company").trim(),

        preferred_contact_method: getFieldValue(
          form,
          "preferred_contact_method",
        ),

        message: getFieldValue(form, "message").trim(),

        marketing_consent: Boolean(marketingConsent?.checked),
      },

      /**
       * The server must treat this information as untrusted context.
       * Shopify Admin GraphQL will provide the authoritative product record.
       */
      product_context: getProductContext(form),

      attribution: getAttribution(),

      page_context: {
        title: document.title,
        current_url: sanitizeUrl(window.location.href),
        referrer: sanitizeUrl(document.referrer),
      },

      device_context: {
        language: navigator.language || "",

        timezone:
          Intl.DateTimeFormat().resolvedOptions().timeZone || "",

        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,

        screen_width: window.screen?.width || 0,
        screen_height: window.screen?.height || 0,
      },
    };
  }

  function createQuoteRequestId() {
    const randomPart = window.crypto?.randomUUID
      ? window.crypto.randomUUID().replaceAll("-", "")
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;

    return `qr_${randomPart}`;
  }

  function normalizeVariantId(value) {
    if (!value) {
      return "";
    }

    const stringValue = String(value);
    const match = stringValue.match(/(\d+)$/);

    return match ? match[1] : stringValue;
  }

  function updateQuoteFormVariant(form, variant) {
    const variantId = normalizeVariantId(variant?.id || variant);

    if (!variantId) {
      return;
    }

    setFieldValue(form, "variant_id", variantId);
    form.dataset.variantId = variantId;

    if (variant?.sku) {
      setFieldValue(form, "sku", variant.sku);
    } else {
      /**
       * Avoid submitting a stale SKU after the customer selects
       * a different variant. The server retrieves the correct SKU.
       */
      setFieldValue(form, "sku", "");
    }
  }

  function getRelevantQuoteForms(sourceElement) {
    const section = sourceElement?.closest?.(
      '[id^="shopify-section-"]',
    );

    if (section) {
      return Array.from(section.querySelectorAll(FORM_SELECTOR));
    }

    return Array.from(document.querySelectorAll(FORM_SELECTOR));
  }

  /**
   * Reads the selected variant from Shopify's existing product form.
   * It does not change or submit the Shopify cart form.
   */
  function syncVariantFromProductForm(sourceElement) {
    window.setTimeout(() => {
      const section = sourceElement?.closest?.(
        '[id^="shopify-section-"]',
      );

      const scope = section || document;

      const productVariantInput = scope.querySelector(
        'form[action*="/cart/add"] [name="id"]',
      );

      if (!productVariantInput?.value) {
        return;
      }

      getRelevantQuoteForms(sourceElement).forEach((form) => {
        updateQuoteFormVariant(form, productVariantInput.value);
      });
    }, 0);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;

    if (form.dataset.submitting === "true") {
      return;
    }

    validateContactFields(form);
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    form.dataset.submitting = "true";

    setSubmitting(form, true);
    setFormStatus(form, "idle");

    /**
     * This event means the customer attempted to submit.
     * It is not yet considered a successful lead.
     */
    pushAnalyticsEvent("quote_form_submit", form);

    const endpoint = form.dataset.quoteEndpoint || form.action;

    try {
      const payload = buildPayload(form);
      // Keep the same durable request ID across a customer retry so the
      // server can resume from the last stored GHL stage without duplicates.
      form.dataset.quoteRequestId = payload.quote_request_id;
      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "same-origin",

        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Voigt-Quote-Request-Id": payload.quote_request_id,
        },

        body: JSON.stringify(payload),
      });

      const contentType =
        response.headers.get("content-type") || "";

      const result = contentType.includes("application/json")
        ? await response.json()
        : null;

      const accepted =
        response.ok &&
        (
          result?.ok === true ||
          result?.success === true ||
          result?.accepted === true
        );

      const quoteRequestId =
        result?.quote_request_id ||
        result?.quoteRequestId ||
        "";

      /**
       * Successful submissions must include the server-generated
       * quote_request_id before generate_lead is fired.
       */
      if (!accepted || !quoteRequestId) {
        const error = new Error(
          ["invalid_name", "invalid_phone", "invalid_email"].includes(result?.error_code)
            ? result.message : "The quote request was not accepted.",
        );

        error.status = response.status;
        error.code =
          result?.error_code || "submission_not_accepted";
        error.referenceCode = result?.reference_code || "";
        error.quoteRequestId = payload.quote_request_id;

        throw error;
      }

      setFormStatus(form, "success");

      /**
       * This is the confirmed conversion event.
       *
       * GTM can use this event for:
       * - GA4
       * - Google Ads
       * - Meta
       * - TikTok
       * - Microsoft Ads
       */
      pushAnalyticsEvent("generate_lead", form, {
        quote_request_id: quoteRequestId,

        value: result?.conversion_value ?? 0,
        currency: result?.currency || "USD",

        first_touch_source:
          result?.first_touch_source || "",

        latest_touch_source:
          result?.latest_touch_source || "",
      });

      form.reset();
      delete form.dataset.quoteRequestId;

      /**
       * Restore the currently selected Shopify variant after reset.
       */
      syncVariantFromProductForm(form);
    } catch (error) {
      const requestSuffix = String(error?.quoteRequestId || "")
        .replace(/^qr_/, "")
        .slice(0, 8)
        .toUpperCase();
      const referenceCode =
        error?.referenceCode ||
        `PDS-Q-9002-${requestSuffix || Date.now().toString(36).toUpperCase()}`;

      setFormStatus(
        form,
        "error",
        ["invalid_name", "invalid_phone", "invalid_email"].includes(error?.code)
          ? error.message
          : `We were unable to submit your request. Please try again or contact Power Drive Supply and reference ${referenceCode}.`,
      );

      pushAnalyticsEvent("quote_form_error", form, {
        error_code:
          error?.code || "network_or_server_error",

        http_status: Number(error?.status || 0),
        reference_code: referenceCode,
      });
    } finally {
      form.dataset.submitting = "false";
      setSubmitting(form, false);
    }
  }

  function initializeViewTracking(form) {
    if (!("IntersectionObserver" in window)) {
      pushAnalyticsEvent("quote_form_view", form);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries.find(
          (entry) => entry.isIntersecting,
        );

        if (!visibleEntry) {
          return;
        }

        pushAnalyticsEvent("quote_form_view", form);
        observer.disconnect();
      },
      {
        threshold: 0.2,
      },
    );

    observer.observe(form);
  }

  function validateContactFields(form) {
    const name = getField(form, "full_name");
    const phone = getField(form, "phone");
    if (name) name.setCustomValidity(name.value.trim() ? "" : "Please enter your full name.");
    if (phone) {
      const raw = phone.value.trim();
      const digits = raw.replace(/\D/g, "");
      const valid = raw.length <= 50 && /^\+?[\d\s().-]+$/.test(raw) &&
        (raw.startsWith("+") ? /^[1-9]\d{7,14}$/.test(digits) : /^(1)?[2-9]\d{2}[2-9]\d{6}$/.test(digits));
      phone.setCustomValidity(valid ? "" : "Enter a valid phone number: 10 digits for US/Canada, or + and country code.");
      const help = form.querySelector("[data-quote-phone-help]");
      if (help) {
        help.textContent = valid ? "" : "Enter 10 digits for US/Canada, or + and country code.";
        help.hidden = valid;
      }
      phone.setAttribute("aria-invalid", String(!valid));
    }
  }

  function simplifyContactFields(form) {
    const first = getField(form, "first_name");
    const last = getField(form, "last_name");
    if (first && !getField(form, "full_name")) {
      first.value = [first.value, last?.value].filter(Boolean).join(" ");
      first.name = "full_name";
      first.autocomplete = "name";
      first.maxLength = 200;
      const label = first.closest(".field")?.querySelector("label");
      if (label) label.textContent = "Full Name *";
    }
    for (const name of ["last_name", "postal_code", "company", "preferred_contact_method", "message", "marketing_consent"]) {
      for (const field of getFields(form, name)) {
        const wrapper = field.closest("fieldset") || field.closest(".field");
        if (wrapper) wrapper.remove();
      }
    }
    for (const name of ["full_name", "email", "phone"]) {
      const field = getField(form, name);
      if (!field) continue;
      field.required = true;
      field.setAttribute("aria-required", "true");
      field.addEventListener("input", () => validateContactFields(form));
    }
    const phone = getField(form, "phone");
    if (phone) {
      phone.maxLength = 50;
      phone.placeholder = "(555) 555-0123";
      const label = phone.closest(".field")?.querySelector("label");
      if (label) label.textContent = "Phone *";
      const help = document.createElement("small");
      help.id = `${phone.id}-help`;
      help.setAttribute("data-quote-phone-help", "");
      help.setAttribute("aria-live", "polite");
      help.textContent = "Enter 10 digits for US/Canada, or + and country code.";
      phone.setAttribute("aria-describedby", help.id);
      phone.insertAdjacentElement("afterend", help);
    }
  }

  function initializeForm(form) {
    if (form.hasAttribute(INITIALIZED_ATTRIBUTE)) {
      return;
    }

    form.setAttribute(INITIALIZED_ATTRIBUTE, "true");
    form.dataset.submitting = "false";
    simplifyContactFields(form);

    /**
     * Some storefront optimizers remove value attributes from hidden inputs.
     * The same public Shopify identifiers are retained on the form wrapper,
     * so restore them before analytics or submission code reads the fields.
     * The server still treats these identifiers as untrusted and validates
     * them through Shopify Admin GraphQL before accepting the request.
     */
    if (!getFieldValue(form, "product_id") && form.dataset.productId) {
      setFieldValue(form, "product_id", form.dataset.productId);
    }

    if (!getFieldValue(form, "variant_id") && form.dataset.variantId) {
      setFieldValue(form, "variant_id", form.dataset.variantId);
    }

    let hasStarted = false;

    const markStarted = (event) => {
      const target = event.target;

      if (
        hasStarted ||
        !(target instanceof HTMLElement) ||
        target.matches(
          'input[type="hidden"], [name="website"]',
        )
      ) {
        return;
      }

      hasStarted = true;
      pushAnalyticsEvent("quote_form_start", form);
    };

    form.addEventListener("focusin", markStarted);
    form.addEventListener("input", markStarted);
    form.addEventListener("change", markStarted);
    form.addEventListener("submit", handleSubmit);

    initializeViewTracking(form);
    syncVariantFromProductForm(form);
  }

  function initializeForms(root = document) {
    if (root.matches?.(FORM_SELECTOR)) {
      initializeForm(root);
    }

    root
      .querySelectorAll?.(FORM_SELECTOR)
      .forEach(initializeForm);
  }

  /**
   * Fallback listener for themes that update the hidden
   * Shopify product-form variant input.
   */
  document.addEventListener("change", (event) => {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    if (
      target.matches(
        [
          'form[action*="/cart/add"] [name="id"]',
          "variant-selects select",
          "variant-radios input",
        ].join(", "),
      )
    ) {
      syncVariantFromProductForm(target);
    }
  });

  /**
   * Shopify's standard product selection event.
   */
  document.addEventListener(
    "shopify:product:select",
    (event) => {
      const promise = event.detail?.promise;

      if (promise && typeof promise.then === "function") {
        promise
          .then((result) => {
            const variant = result?.variant;

            if (!variant) {
              return;
            }

            getRelevantQuoteForms(event.target).forEach(
              (form) => {
                updateQuoteFormVariant(form, variant);
              },
            );
          })
          .catch(() => {
            syncVariantFromProductForm(event.target);
          });

        return;
      }

      const variant = event.detail?.variant;

      if (variant) {
        getRelevantQuoteForms(event.target).forEach(
          (form) => {
            updateQuoteFormVariant(form, variant);
          },
        );
      } else {
        syncVariantFromProductForm(event.target);
      }
    },
  );

  /**
   * Reinitialize when Shopify's theme editor reloads a section.
   */
  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeForms(event.target);
    },
  );

  window.VoigtProductQuoteRequest = {
    initialize: initializeForms,
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initializeForms(),
    );
  } else {
    initializeForms();
  }
})();
